import React, { Component } from "react";
import styled from "styled-components";
import { Play } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { getUID } from "../../../Helper";

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(5, 1fr);
  }
`;
const SkeletonLoader = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    rgba(190, 190, 190, 0.2) 25%,
    rgba(129, 129, 129, 0.24) 37%,
    rgba(190, 190, 190, 0.2) 63%
  );
  background-size: 400% 100%;
  animation: skeleton-loading 1.4s ease infinite;
  border-radius: 8px;

  @keyframes skeleton-loading {
    0% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }
`;

const GameOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(5px);
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.2s ease;
`;

const PlayIconContainer = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const FullScreenContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 999;
  background-color: #292d2e;
`;

const FullScreenIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  display: ${(props) => (props.loading ? "none" : "block")};
`;

const PlayIcon = styled(Play)`
  color: #fff;
  font-size: 24px;
`;

const BackButton = styled.button`
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 1000;
  background: none;
  border: none;
  color: #fff;
  font-size: 24px;
  cursor: pointer;
  padding: 8px;

  &:hover {
    opacity: 0.8;
  }
`;


const LoadMoreButton = styled.button`
  background-color: #292d2e;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: bold;
  margin: 20px auto;
  display: block;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #4a5354;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #292d2e;
  border-top: 4px solid #232626;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;
const GridWrapper = styled.div`
  display: grid;
  gap: 12px;
  padding: 12px;
  width: 100%;

  /* Mobile - 3 items per row */
  grid-template-columns: repeat(3, 1fr);

  /* Tablet - 4 items per row */
  @media (min-width: 640px) {
    grid-template-columns: repeat(4, 1fr);
  }

  /* Desktop - 6 items per row */
  @media (min-width: 1024px) {
    grid-template-columns: repeat(6, 1fr);
    gap: 16px;
    padding: 16px;
  }

  /* Large Desktop - 8 items per row */
  @media (min-width: 1280px) {
    grid-template-columns: repeat(8, 1fr);
    gap: 20px;
    padding: 20px;
  }
`;

const LotteryGameCard = styled.div`
  width: 100%;
  aspect-ratio: 1.32;
  border-radius: 8px;
  position: relative;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  transition: transform 0.2s ease;

  &:hover {
    ${GameOverlay} {
      opacity: 1;
    }
    transform: translateY(-5px);
  }
`;

const LotteryGameImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
`;

class CrashLiveGames extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLogged: localStorage.getItem("logged") === "true",
      displayGamesCrashXgaming: [], // Standardized state key
      message: null,
      iframeUrl: "",
      showIframe: false,
      iframeLoading: false,
      isLoadingMore: false, // For "Load More" loading state
      crashPage: 1, // Track pagination page
      loading: true, // For initial load spinner
      loadedImages: new Set(),
    };
  }
  handleImageLoad = (gameId) => {
    this.setState(prevState => ({
      loadedImages: new Set(prevState.loadedImages).add(gameId)
    }));
  };
  async componentDidMount() {
    await this.fetchInitialCrashGames();
  }

  fetchVendorData = async ({ vendorName, page = 1, perPage = 24 }) => {
    const baseUrl = "https://bkk07.com/xGaming/by-vendor";
    const vendorCategories = {
      crash: [
        "spadegaming",
        "platipus",
        "smartsoft",
        "aviatrix",
        "mancala",
        "elbet",
        "jetx",
        "aviator",
        "bgaming",
      ],
    };

    const createUrl = (vendor) =>
      `${baseUrl}?vendor=${vendor}&page=${page}&per_page=${perPage}`;

    const fetchSingleVendor = async (vendor) => {
      try {
        const response = await fetch(createUrl(vendor));
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);
        return { vendor, data: await response.json() };
      } catch (error) {
        console.error(`Error fetching data for vendor ${vendor}:`, error);
        return { vendor, error: error.message };
      }
    };

    try {
      if (vendorCategories[vendorName]) {
        const vendors = vendorCategories[vendorName];
        if (vendorName.toLowerCase() === "crash") {
          const results = await Promise.all(
            vendors.map((vendor) => fetchSingleVendor(vendor))
          );
          return {
            category: vendorName,
            results: results,
          };
        }

        const results = [];
        for (const vendor of vendors) {
          const result = await fetchSingleVendor(vendor);
          results.push(result);
        }
        return {
          category: vendorName,
          results: results,
        };
      }

      const result = await fetchSingleVendor(vendorName);
      return {
        category: "single",
        results: [result],
      };
    } catch (error) {
      console.error("Error in fetchVendorData:", error);
      throw new Error(`Failed to fetch vendor data: ${error.message}`);
    }
  };

  fetchInitialCrashGames = async () => {
    try {
      this.setState({ loading: true }); // Show loader during initial fetch
      const response = await this.fetchVendorData({
        vendorName: "crash",
        page: 1,
        perPage: 24, // Match with "Load More" for consistency
      });

      const crashGames = response?.results.reduce((result, vendorObj) => {
        if (
          vendorObj.data &&
          vendorObj.data.success === true &&
          Array.isArray(vendorObj.data.data)
        ) {
          const vendorCrashGames = vendorObj.data.data.filter(
            (game) => game.type === "crash" || game.type === "crashgame"
          );
          return [...result, ...vendorCrashGames];
        }
        return result;
      }, []);

      this.setState({
        displayGamesCrashXgaming: crashGames,
        crashPage: 1,
        loading: false, // Hide loader after fetch
      });
    } catch (error) {
      console.error("Error fetching initial crash games:", error);
      this.setState({
        message: {
          type: "error",
          text: "Failed to load crash games. Please try again.",
        },
        loading: false, // Hide loader even on error
      });
    }
  };

  loadMoreCrashGames = async () => {
    this.setState({ isLoadingMore: true });
    const nextPage = this.state.crashPage + 1;

    try {
      const response = await this.fetchVendorData({
        vendorName: "crash",
        page: nextPage,
        perPage: 24, // Consistent with initial fetch
      });

      const newCrashGames = response?.results.reduce((result, vendorObj) => {
        if (
          vendorObj.data &&
          vendorObj.data.success === true &&
          Array.isArray(vendorObj.data.data)
        ) {
          const vendorCrashGames = vendorObj.data.data.filter(
            (game) => game.type === "crash" || game.type === "crashgame"
          );
          return [...result, ...vendorCrashGames];
        }
        return result;
      }, []);

      this.setState((prevState) => ({
        displayGamesCrashXgaming: [
          ...prevState.displayGamesCrashXgaming,
          ...newCrashGames,
        ],
        crashPage: nextPage,
        isLoadingMore: false,
      }));
    } catch (error) {
      console.error("Error loading more crash games:", error);
      this.setState({
        isLoadingMore: false,
        message: {
          type: "error",
          text: "Failed to load more crash games. Please try again.",
        },
      });
    }
  };

  handleIframeLoad = () => {
    this.setState({ iframeLoading: false });
  };

  handleBackButton = () => {
    this.setState({ showIframe: false, iframeUrl: "", iframeLoading: true });
  };

  handleGameLaunchXGaming = async (game) => {
    if (!this.state.isLogged) {
      this.setState({
        message: { type: "error", text: "Please login to play live game" },
      });
      return;
    }

    try {
      const response = await fetch("https://bkk07.com/api/casino/gamerun", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          game_id: game.id,
          currency: "USD",
          mode: "real",
          language: "en",
          home_url: "https://bkk07.com",
          device: "desktop",
          user_id: getUID,
          vendor: game.vendor,
          title: game.title,
          coin: "usdt",
        }),
      });

      const result = await response.json();
      if (result.gameRunUrl) {
        this.setState({
          iframeUrl: result.gameRunUrl,
          showIframe: true,
          iframeLoading: true,
        });
      } else {
        throw new Error("Failed to launch game");
      }
    } catch (error) {
      this.setState({ message: { type: "error", text: error.message } });
    }
  };

  render() {
    const {
      displayGamesCrashXgaming,
      showIframe,
      iframeUrl,
      iframeLoading,
      isLoadingMore,
      loadedImages,
      loading, // Use for initial loader
    } = this.state;

    const imageStyle = {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: "250px",
      animation: "fadeinout 2s infinite",
    };

    const animationStyle = `
      @keyframes fadeinout {
        0%, 100% { opacity: 0; }
        50% { opacity: 1; }
      }
    `;

    if (showIframe) {
      return (
        <FullScreenContainer>
          <style>{animationStyle}</style>
          {iframeLoading && (
            <img src="assets/images/lg.png" alt="Loading" style={imageStyle} />
          )}
          <FullScreenIframe
            src={iframeUrl}
            title="Game Iframe"
            onLoad={this.handleIframeLoad}
            loading={iframeLoading}
          />
          <BackButton onClick={this.handleBackButton}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </BackButton>
        </FullScreenContainer>
      );
    }

    return (

      <>
        <GridWrapper>
          {displayGamesCrashXgaming?.map((game, index) => (
            <LotteryGameCard
              key={game.id}
              onClick={() => this.handleGameLaunchXGaming(game)}
            >
              {!loadedImages.has(game.id) && <SkeletonLoader />}
              <LotteryGameImage
                src={game.details_thumbnails_300x300}
                alt={`Game ${index}`}
                onLoad={() => this.handleImageLoad(game.id)}
                style={{
                  display: loadedImages.has(game.id) ? 'block' : 'none'
                }}
              />
              <GameOverlay>
                <PlayIconContainer>
                  <PlayIcon />
                </PlayIconContainer>
              </GameOverlay>
            </LotteryGameCard>
          ))}
        </GridWrapper>
        {isLoadingMore ? (
          <LoaderContainer>
            <Spinner />
          </LoaderContainer>
        ) : (
          <LoadMoreButton
            onClick={this.loadMoreCrashGames}
            disabled={isLoadingMore}
          >
            Load More
          </LoadMoreButton>
        )}
      </>

    );
  }
}

export default CrashLiveGames;